import { useCallback, useMemo } from 'react';
import { isGeneratingPreviewSelector } from 'blocks/ClipSelect/selectors';
import { ClipSuggestion } from 'blocks/ClipSelect/types';
import useSuggestionsEdits from './useSuggestionsEdits';

export type UseSuggestionsProps = {
  suggestedClips: ClipSuggestion[];
};

/**
 * Hook to manage suggestion state
 */
export default function useSuggestions({
  suggestedClips,
}: UseSuggestionsProps) {
  const {
    removedSuggestionIds,
    updatedSuggestionProjectNames,
    removeSuggestion,
    updateSuggestionProjectName,
  } = useSuggestionsEdits();

  const suggestionIds = useMemo(() => suggestedClips.map(clip => clip.id), [
    suggestedClips,
  ]);

  const visibleSuggestionIds = useMemo(
    () => suggestionIds?.filter(id => !removedSuggestionIds.includes(id)),
    [removedSuggestionIds, suggestionIds],
  );

  const hasClipSuggestions = !!suggestionIds?.length;

  const findSuggestion = useCallback(
    (id: number) => suggestedClips.find(c => c.id === id),
    [suggestedClips],
  );

  return {
    findSuggestion,
    hasClipSuggestions,
    removeSuggestion,
    updateSuggestionProjectName,
    suggestedClips,
    suggestionIds,
    isGeneratingPreviews: suggestedClips?.some(isGeneratingPreviewSelector),
    visibleSuggestionIds,
    updatedSuggestionProjectNames,
  };
}
